import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { Input } from "@shared/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import {
  Pencil,
  Plus,
  Search,
  Calendar,
  User,
  Check,
  X,
  Clock,
  AlertTriangle,
  Wrench,
  Camera,
  Loader2,
  AlertCircle,
  CheckSquare,
} from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Alert, AlertDescription } from "@shared/ui/alert";
import {
  useTasks,
  useCreateTask,
  useUpdateTask,
  useToggleTaskStatus,
  useSkills,
  useOperationTypes,
} from "../../../../hooks";
import { Task } from "../../../../types";

// ====================== TYPES ======================

interface TaskFormData {
  name: string;
  operationType: string;
  turnaroundTime: string;
  downtime: string;
  skillIds: number[];
  photoRequired: boolean;
}

// ====================== HOOKS ======================

// Custom hook for debounced search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// ====================== COMPONENTS ======================

// Empty state component
const EmptyState: React.FC<{
  hasSearchQuery: boolean;
  onAddNew: () => void;
}> = ({ hasSearchQuery, onAddNew }) => {
  return (
    <div className="col-span-full py-12 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
      <div className="flex flex-col items-center gap-2">
        <Search className="h-10 w-10 text-muted-foreground/50" />
        <h4>No tasks found</h4>
        <p className="text-sm">
          {hasSearchQuery
            ? "Try adjusting your search or add a new task"
            : "No tasks available"}
        </p>
        <Button variant="outline" className="mt-2" onClick={onAddNew}>
          <Plus className="mr-2 h-4 w-4" /> Add New Task
        </Button>
      </div>
    </div>
  );
};

// ====================== MAIN COMPONENT ======================

const TasksTab: React.FC = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState<Task | null>(null);

  // Add state to track if we're in cancel mode
  const [isCanceling, setIsCanceling] = useState(false);

  // Get search query from URL
  const searchQuery = searchParams.get("search") || "";

  // Debounce the search input for better UX (but server handles the actual search)
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debouncedSearchInput = useDebounce(searchInput, 500);

  // Update URL when debounced search changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (debouncedSearchInput.trim()) {
      newSearchParams.set("search", debouncedSearchInput.trim());
    } else {
      newSearchParams.delete("search");
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [debouncedSearchInput, searchParams, setSearchParams]);

  // API hooks - now using server-side search
  const { data: tasks = [], isLoading, error, refetch } = useTasks(searchQuery);
  const { data: operationTypes = [], isLoading: loadingOperationTypes } =
    useOperationTypes();
  const { data: skills = [], isLoading: loadingSkills } = useSkills();
  const createTaskMutation = useCreateTask();
  const updateTaskMutation = useUpdateTask();
  const toggleStatusMutation = useToggleTaskStatus();

  // Transform operation types for form options
  const operationTypeOptions = useMemo(
    () =>
      operationTypes.map((type) => ({
        value: type.key.toString(),
        label: type.value,
      })),
    [operationTypes],
  );

  // Transform skills for form options
  const skillOptions = useMemo(
    () =>
      skills
        .filter((skill) => skill.isActive)
        .map((skill) => ({
          value: skill.id,
          label: skill.name,
        })),
    [skills],
  );

  // Form fields configuration
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "operationType",
        type: "select",
        label: "Operation Type",
        placeholder: loadingOperationTypes
          ? "Loading operation types..."
          : "Select operation type...",
        required: true,
        disabled: !!currentTask,
        options: operationTypeOptions,
        helperText: "Choose the type of operation for this task",
      },
      {
        name: "name",
        type: "text",
        label: "Task Name",
        placeholder: "Enter task name...",
        required: true,
        minLength: 2,
        maxLength: 50,
        helperText: "Provide a clear, descriptive name for the task",
      },
      {
        name: "turnaroundTime",
        type: "number",
        label: "Turnaround Time (minutes)",
        placeholder: "30",
        required: true,
        min: 1,
        max: 9999,
        suffix: <Clock className="h-4 w-4 text-muted-foreground" />,
        helperText: "Expected time to complete this task",
      },
      {
        name: "downtime",
        type: "number",
        label: "Downtime (minutes)",
        placeholder: "5",
        required: true,
        min: 0,
        max: 9999,
        suffix: <AlertTriangle className="h-4 w-4 text-muted-foreground" />,
        helperText: "Expected downtime caused by this task",
      },
      {
        name: "skillIds",
        type: "multi-select",
        label: "Required Skills",
        placeholder: "Select required skills...",
        required: true,
        disabled: loadingSkills,
        options: skillOptions,
        searchable: true,
        clearable: true,
        maxDisplayItems: 3,
        helperText: "Choose skills required to perform this task",
      },
      {
        name: "photoRequired",
        type: "switch",
        label: "Photo Required",
        helperText: "Enable if this task requires photo documentation",
        defaultValue: false,
      },
    ],
    [
      operationTypeOptions,
      skillOptions,
      loadingOperationTypes,
      loadingSkills,
      currentTask,
    ],
  );

  // Create form instance using FormService - following established pattern
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      operationType: "",
      turnaroundTime: "",
      downtime: "",
      skillIds: [],
      photoRequired: false,
    },
  });

  // Update form when editing a task - but not during cancel
  useEffect(() => {
    if (isCanceling) return; // Skip form updates during cancel

    if (currentTask && isEditModalOpen) {
      // Find the operation type key that matches the current task's operation type
      const currentOperationType = operationTypes.find(
        (op) => op.value === currentTask.workOrderType,
      );

      const populateData = {
        name: currentTask.name,
        operationType: currentOperationType?.key?.toString() || "",
        turnaroundTime: currentTask.turnaroundTime.toString(),
        downtime: currentTask.downtime.toString(),
        skillIds: currentTask.skillIds || [],
        photoRequired: currentTask.photoRequired || false,
      };

      FormService.populateForm(form, populateData, formFields);
    } else if (isAddModalOpen) {
      const defaultValues = {
        name: "",
        operationType: "",
        turnaroundTime: "",
        downtime: "",
        skillIds: [],
        photoRequired: false,
      };
      FormService.populateForm(form, defaultValues, formFields);
    }
  }, [
    currentTask,
    isEditModalOpen,
    isAddModalOpen,
    form,
    formFields,
    operationTypes,
    isCanceling,
  ]);

  // Handle field changes - simplified like other components
  const handleFieldChange = useCallback(
    (fieldName: string, value: any) => {
      // Prevent any field changes during cancel
      if (isCanceling) return;
    },
    [isCanceling],
  );

  // Handle form submission
  const handleSubmit = async (data: TaskFormData) => {
    if (isCanceling) return; // Prevent submit during cancel

    try {
      if (currentTask) {
        const updatePayload = {
          code: currentTask.id,
          name: data.name.trim(),
          operationType: parseInt(data.operationType),
          turnaroundTime: parseInt(data.turnaroundTime),
          downtime: parseInt(data.downtime),
          skillIds: data.skillIds || [],
          photoRequired: data.photoRequired || false,
          isActive: currentTask.isActive,
        };
        // Update existing task
        await updateTaskMutation.mutateAsync(updatePayload);
        setIsEditModalOpen(false);
      } else {
        const createPayload = {
          name: data.name.trim(),
          operationType: parseInt(data.operationType),
          turnaroundTime: parseInt(data.turnaroundTime),
          downtime: parseInt(data.downtime),
          skillIds: data.skillIds || [],
          photoRequired: data.photoRequired || false,
        };
        // Create new task
        await createTaskMutation.mutateAsync(createPayload);
        setIsAddModalOpen(false);
      }

      // Reset form state - following established pattern
      FormService.resetForm(form, formFields);
      setCurrentTask(null);
    } catch (error) {
      // Handle error
    }
  };

  const handleReset = useCallback(() => {
    if (isCanceling) return; // Prevent reset during cancel
    FormService.resetForm(form, formFields);
  }, [form, formFields, isCanceling]);

  // Event handlers
  const handleEditClick = useCallback((task: Task) => {
    setCurrentTask(task);
    setIsEditModalOpen(true);
  }, []);

  const handleToggleActive = useCallback(
    async (task: Task) => {
      try {
        await toggleStatusMutation.mutateAsync({
          code: task.id,
          isActive: !task.isActive,
          name: task.name,
          operationType: task.operationType,
        });
      } catch (error) {
        // Handle error
      }
    },
    [toggleStatusMutation],
  );

  // Handle modal close with cancel protection
  const handleAddModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsAddModalOpen(false);
    setCurrentTask(null);

    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  const handleEditModalClose = useCallback(() => {
    setIsCanceling(true);
    setIsEditModalOpen(false);
    setCurrentTask(null);

    // Reset form without triggering side effects
    setTimeout(() => {
      FormService.resetForm(form, formFields);
      setIsCanceling(false);
    }, 0);
  }, [form, formFields]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);
    },
    [],
  );

  // Get skills display text
  const getSkillsDisplayText = useCallback(
    (skillIds: number[]) => {
      if (skillIds.length === 0) return "No skills required";
      const taskSkills = skills.filter((skill) => skillIds.includes(skill.id));
      return taskSkills.map((skill) => skill.name).join(", ");
    },
    [skills],
  );

  return (
    <div className="">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background pt-2 pb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start flex-col">
          <h3 className="page-subheading">Tasks</h3>
          <p className="page-subtitle">Create and manage Task.</p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          disabled={createTaskMutation.isPending || loadingOperationTypes}
        >
          {createTaskMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Add New Task
        </Button>
      </div>

      {/* Grid */}
      <div className="relative pb-4 mt-2">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search tasks..."
          className="pl-8 w-full sm:w-[250px]"
          value={searchInput}
          onChange={handleSearchChange}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.isArray(tasks) &&
          tasks.map((task) => (
            <Card
              key={task.id}
              className="overflow-hidden transition-all hover:shadow-md hover:border-primary/20 group"
            >
              <CardContent className="p-0">
                <div className="p-4 border-b bg-muted/20">
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-2">
                      <CheckSquare className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium">{task.name}</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Type: {task.workOrderType}
                        </p>
                        {task.photoRequired && (
                          <div className="flex items-center gap-1 mt-1">
                            <Camera className="h-3 w-3 text-blue-600" />
                            <span className="text-xs text-blue-600">
                              Photo Required
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditClick(task)}
                      className="opacity-0 group-hover:opacity-100 hover:bg-background/80"
                      disabled={updateTaskMutation.isPending}
                    >
                      {updateTaskMutation.isPending &&
                      currentTask?.id === task.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Pencil className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="p-4">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="h-3.5 w-3.5 text-blue-600" />
                      <span className="text-muted-foreground">Turnaround:</span>
                      <span className="font-medium">
                        {task.turnaroundTime} mins
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-sm">
                      <AlertTriangle className="h-3.5 w-3.5 text-orange-600" />
                      <span className="text-muted-foreground">Downtime:</span>
                      <span className="font-medium">{task.downtime} mins</span>
                    </div>

                    <div className="flex items-start gap-2 text-sm">
                      <Wrench className="h-3.5 w-3.5 text-green-600 mt-0.5" />
                      <div>
                        <span className="text-muted-foreground">Skills:</span>
                        <p className="font-medium text-wrap">
                          {getSkillsDisplayText(task.skillIds)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t">
                    <div className="flex gap-2 items-center text-sm text-muted-foreground mb-2">
                      <User className="h-3.5 w-3.5" />
                      <p>{task.createdBy}</p>
                    </div>
                    <div className="flex gap-2 items-center text-sm text-muted-foreground mb-4">
                      <Calendar className="h-3.5 w-3.5" />
                      <p>{task.createdDate}</p>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t">
                    <span className="text-sm">
                      {task.isActive ? (
                        <span className="flex items-center gap-1.5 text-green-600 bg-green-50 dark:bg-green-950/30 px-2 py-1 rounded-full text-xs font-medium">
                          <Check className="h-3 w-3" /> Active
                        </span>
                      ) : (
                        <span className="flex items-center gap-1.5 text-gray-600 bg-gray-50 dark:bg-gray-800/30 px-2 py-1 rounded-full text-xs font-medium">
                          <X className="h-3 w-3" /> Inactive
                        </span>
                      )}
                    </span>
                    <Switch
                      checked={task.isActive}
                      onCheckedChange={() => handleToggleActive(task)}
                      disabled={toggleStatusMutation.isPending}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

        {Array.isArray(tasks) && tasks.length === 0 && (
          <EmptyState
            hasSearchQuery={!!searchQuery}
            onAddNew={() => setIsAddModalOpen(true)}
          />
        )}
      </div>

      {/* Add Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={handleAddModalClose}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Add New Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={createTaskMutation.isPending}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleAddModalClose}
                    disabled={createTaskMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={
                      createTaskMutation.isPending || loadingOperationTypes
                    }
                  >
                    {createTaskMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Save Task"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={handleEditModalClose}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={updateTaskMutation.isPending}
              gridColumns={2}
              responsiveColumns={{
                sm: 1,
                md: 2,
              }}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleEditModalClose}
                    disabled={updateTaskMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={
                      updateTaskMutation.isPending || loadingOperationTypes
                    }
                  >
                    {updateTaskMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Task"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksTab;
